import { DataDate } from "../model/data_date";
import { Series } from "../model/series";
import { tablemill } from "../tablemill_metal";
import { MatchesForm } from "../view/matches_form";
import { CombinationRankingTableController } from "./combination_ranking_table_controller";
import { RelatedCombinationRecipesController } from "./related_combination_recipes_controller";
import $ from "jquery";

class MatchesController {
  protected form: MatchesControllerForm;
  protected view: any;
  protected combinationRankingTableController: CombinationRankingTableController;
  protected relatedRecipesController: RelatedCombinationRecipesController;
  private selection: ItemSelection;

  constructor() {
    this.form = new MatchesControllerForm();
    this.selection = this.form.selection;
    this.view = new MatchesForm();
    this.combinationRankingTableController =
      new CombinationRankingTableController(
        this.form,
        "ksi",
        this.rankingTableElement(),
      );
    this.relatedRecipesController = new RelatedCombinationRecipesController();
  }

  protected formElement(): JQuery<HTMLElement> {
    return $("#matches_form");
  }

  protected typeElement(): JQuery<HTMLElement> {
    return $("#type");
  }

  protected partitionElement(): JQuery<HTMLElement> {
    return $("#partition");
  }

  protected canvasElement(): JQuery<HTMLElement> {
    return $("#ksi_graph");
  }

  protected rankingTableElement(): JQuery<HTMLElement> {
    return $("#ksi_ranking_table");
  }

  onLoad(): void {
    this.initialize();
    if (!this.form.isKeywordFound()) {
      this.view.showKeywordNotFoundError(this.view.keywordValue());
      this.combinationRankingTableController.view.hideIndicator();
      this.relatedRecipesController.view.showNotFoundMessage();
      return;
    }
    this.render();
  }

  private initialize(): void {
    this.selection = this.form.selection;
    this.view.initialize(this.form.invalidParameterCombinations());
    this.combinationRankingTableController.initialize();
    this.relatedRecipesController.initialize();
    this.registerEventHandlers();
  }

  private registerEventHandlers(): void {
    this.form.onSubmit((_e: JQuery.SubmitEvent) => {
      if (this.view.keyword.isChanged()) {
        this.selection.clear();
      }
      return true;
    });

    this.view.onAspectChange((aspectElementId: string) => {
      this.view.itemAspect.flipSelectedAspect(aspectElementId);
      this.combinationRankingTableController.updateRankingTable(
        $(`#${aspectElementId}`).data("aspect"),
      );
      return false;
    });

    this.combinationRankingTableController.onItemLinkClick(
      (itemId: number, itemName: string) => {
        this.selectItem(itemId, itemName);
        return false;
      },
    );
  }

  private selectItem(itemId: number, itemName: string): void {
    if (!this.view.graph.hasSeries(itemName)) {
      this.selection.add(itemId);
      this.form.submit();
    }
  }

  private highlightSelection(): void {
    this.selection.items().forEach(() => {
      this.combinationRankingTableController.highlightSelection();
    });
  }

  protected render(): void {
    const selectedAspectId = this.view.itemAspect.selectedAspectId();
    this.combinationRankingTableController
      .render(selectedAspectId)
      .then(
        (_res: any) => {
          return this.renderGraphs();
        },
        (_res: any) => {
          // error handling
        },
      )
      .then((_res: any) => {
        this.relatedRecipesController.setPairingItemIds(this.selection.items());
        return this.relatedRecipesController.render();
      })
      .then(
        (_res: any) => {
          if (this.view.graph.hasNoData()) {
            this.view.showNoDataMessage();
          } else {
            this.view.render();
          }
        },
        (res: any) => {
          this.view.showHttpErrorMessage(res);
        },
      )
      .then((_res: any) => {
        this.view.updateCsvUrl(
          this.form
            .newCombinationDataSeriesQuery(null, this.selection.items())
            .csvUrl(),
        );
      });

    this.view.onSeriesRemoval((_index: number, series: any) => {
      this.selection.remove(series.itemId);
      $("#kumiawase_keyword").val("");
      this.form.submit();
    });
  }

  private renderGraphs(): JQuery.Promise<any> {
    const graphPromises = this.selection
      .items()
      .map((pairingItemId: number) => this.renderEachGraph(pairingItemId));

    return $.when(...graphPromises).done((...results: any[]) => {
      results.forEach((series: any) => this.view.graph.addSeries(series));
    });
  }

  private renderEachGraph(pairingItemId: number): JQuery.Promise<any> {
    const dfd = $.Deferred<any>();
    const query = this.form.newCombinationDataSeriesQuery(pairingItemId);
    query.execute().then(
      (series: any) => {
        series.itemId = pairingItemId;
        dfd.resolve(series);
      },
      (_res: any) => {
        dfd.reject();
      },
    );
    return dfd.promise();
  }
}

class MatchesControllerForm {
  selection: ItemSelection;

  constructor() {
    this.selection = new ItemSelection();
  }

  formElement(): JQuery<HTMLElement> {
    return $("#matches_form");
  }

  submit(): void {
    this.formElement().trigger("submit");
  }

  onSubmit(f: (e: JQuery.SubmitEvent) => boolean): void {
    this.formElement().on("submit", f);
  }

  combinationDataSeriesUrl(): string {
    return $("#ksi_graph").data("ajaxUrl");
  }

  combinationRankingTableUrl(): string {
    return $("#ksi_ranking_table").data("ajaxUrl");
  }

  type(): string {
    return $("#type").val() as string;
  }

  partition(): string {
    return $("#partition").val() as string;
  }

  isKeywordFound(): boolean {
    return !$("#keyword").data("notFound");
  }

  targetDataDate(): any {
    return DataDate.parseYMD($("#target").val() as string, this.partition());
  }

  dimension(): string {
    return $("#dimension").val() as string;
  }

  area(): string {
    return $("#area").val() as string;
  }

  category(): string {
    return $("#category").val() as string;
  }

  keyword(): string {
    return $("#keyword").val() as string;
  }

  itemId(): string {
    return $("#item_id").val() as string;
  }

  maxRank(): string {
    return $("#max_rank").val() as string;
  }

  startDate(): string {
    return $("#ranking_start").val() as string;
  }

  endDate(): string {
    return $("#ranking_end").val() as string;
  }

  newCombinationDataSeriesQuery(
    pairingItemId?: number | null,
    selection?: number[],
  ): CombinationDataSeriesQuery {
    return new CombinationDataSeriesQuery(this, pairingItemId, selection);
  }

  newCombinationRankingTableQuery(aspectId: number): any {
    return new CombinationRankingTableQuery(this, aspectId);
  }

  invalidParameterCombinations(): any {
    return this.formElement().data("invalidParameterCombinations");
  }
}

class ItemSelection {
  private static readonly ITEM_CLASS = "selectingItemId";

  private containerElement(): JQuery<HTMLElement> {
    return $("#itemSelection");
  }

  items(): number[] {
    return $(`.${ItemSelection.ITEM_CLASS}`)
      .map((i, elem) => parseInt($(elem).val() as string, 10))
      .get();
  }

  add(itemId: number): void {
    const strItemId = itemId.toString();
    if (this.items().indexOf(parseInt(strItemId, 10)) === -1) {
      const id = parseInt(strItemId, 10);
      const input = `<input class='${ItemSelection.ITEM_CLASS}' name='selection[]' type='hidden' value='${id}'>`;
      this.containerElement().append(input);
    }
  }

  remove(itemId: number): void {
    $(`.${ItemSelection.ITEM_CLASS}[value*='${itemId}']`).remove();
  }

  clear(): void {
    this.containerElement().empty();
  }
}

class CombinationDataSeriesQuery {
  private form: MatchesControllerForm;
  private pairingItemId?: number | null;
  private selection?: number[];

  constructor(
    form: MatchesControllerForm,
    pairingItemId?: number | null,
    selection?: number[],
  ) {
    this.form = form;
    this.pairingItemId = pairingItemId;
    this.selection = selection;
  }

  execute(): JQuery.Promise<any> {
    return $.ajax({
      url: this.form.combinationDataSeriesUrl(),
      dataType: "json",
      data: this.makeParams(),
    }).then((res: any) => {
      return new Series(res.values, res.name, this.startDate());
    });
  }

  private makeParams(): any {
    const params: any = {
      type: this.form.type(),
      partition: this.form.partition(),
      start: this.startDate().ymd(),
      end: this.form.targetDataDate().ymd(),
      dimension: this.form.dimension(),
      area: this.form.area(),
      category: this.form.category(),
    };

    if (this.pairingItemId != null) {
      params.pairing_item_id = this.pairingItemId;
    } else {
      params.pairing_item_ids = this.selection;
    }

    return params;
  }

  private startDate(): any {
    const end = this.form.targetDataDate();
    switch (end.partition) {
      case "weekly":
        return end.partitionStartDate().weeksAgo(11);
      case "monthly":
        return end.partitionStartDate().yearsAgoByDays(0.97);
      case "yearly":
        return new DataDate(tablemill.FIRST_DATA_DATE, "yearly");
      default:
        return end.partitionStartDate();
    }
  }

  csvUrl(): string {
    return `/combinations/${this.form.itemId()}/stats.csv?${$.param(this.makeParams())}`;
  }
}

class CombinationRankingTableQuery {
  private form: MatchesControllerForm;
  private aspectId: number;

  constructor(form: MatchesControllerForm, aspectId: number) {
    this.form = form;
    this.aspectId = aspectId;
  }
}

export { MatchesController, MatchesControllerForm };
